import { OUTCOMES, SPOKE_WITH, TOUCH_CHANNELS } from '../../../shared/enums';
import type { ExportColumn, ExportProfile } from '../../../shared/export';
import { DEFAULT_EXPORT_PROFILE, profileProblem } from '../../../shared/export';
import { shell } from '../app';
import { loadExportProfile, saveExportProfile } from '../data/activity';
import { btn, empty, input, section, textarea } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import { CHANNEL_LABELS, OUTCOME_LABELS, SPOKE_WITH_LABELS } from '../ui/labels';
import type { View } from '../ui/router';
import { navigate } from '../ui/router';

/**
 * The Salesforce field map (D91). Artur sees Square's real Salesforce org after Nov 2; until then
 * every header and picklist word here is a guess, so all of them are his to change. The app can
 * only export what it records, so the list of fields is fixed — what goes out, under which name,
 * and as which word is not. Nothing here connects to Salesforce.
 */

const FIELD_NAMES: Record<ExportColumn['field'], string> = {
  subject: 'Subject line',
  company: 'Business name',
  type: 'How the touch happened',
  status: 'Status (always Completed)',
  priority: 'Priority (always Normal)',
  activity_date: 'Day of the touch',
  activity_time: 'Time of the touch',
  call_result: 'How it went',
  spoke_with: 'Who he spoke with',
  persona: 'Persona',
  message_variant: 'Message used',
  description: 'Notes',
  next_step: 'Next step',
  next_step_date: 'Next-touch date',
  contact_name: 'Owner name',
  phone: 'Phone',
  email: 'Email',
  street: 'Street',
  city: 'City',
  postal_code: 'Postal code',
  place_id: 'Place ID',
  lead_stage: 'Stage in our pipeline',
  lead_source: 'Lead source',
  crm_lead_id: 'Our lead id',
  crm_touch_id: 'Our touch id',
};

export const activityFieldsView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Salesforce fields', { back: '#/activity' });
  const host = h('div', { class: 'page page--fields' });
  root.appendChild(host);
  let profile: ExportProfile | null = null;
  let disposed = false;

  const save = btn('Save', { variant: 'primary', size: 'lg', class: 'w-full' });
  save.addEventListener('click', () => {
    if (!profile) return;
    // the API's own bounds, mirrored here so a blank box is named instead of coming back as a 400
    const problem = profileProblem(profile);
    if (problem) {
      shell.toast(problem.message, { kind: 'error', duration: 5000 });
      host.querySelector<HTMLElement>(`[data-field="${problem.field}"]`)?.focus();
      return;
    }
    save.disabled = true;
    save.textContent = 'Saving…';
    void saveExportProfile(profile)
      .then(() => {
        shell.toast('Fields saved', { kind: 'success' });
        navigate('#/activity');
      })
      .catch((err: unknown) => {
        shell.toast(
          `Could not save: ${err instanceof Error ? err.message : String(err)} — the mapping lives on the server, so this one needs signal.`,
          { kind: 'error', duration: 6000 },
        );
        save.disabled = false;
        save.textContent = 'Save';
      });
  });

  const columnRow = (col: ExportColumn) =>
    h(
      'div',
      { class: 'fieldrow' },
      h(
        'label',
        { class: 'fieldrow__on' },
        h('input', {
          type: 'checkbox',
          checked: col.on,
          'aria-label': `Include ${FIELD_NAMES[col.field]}`,
          onChange: (ev: Event) => (col.on = (ev.currentTarget as HTMLInputElement).checked),
        }),
        h('span', { class: 'fieldrow__name' }, FIELD_NAMES[col.field]),
      ),
      input({
        value: col.header,
        maxlength: 60,
        class: 'input--sm',
        ariaLabel: `Salesforce column for ${FIELD_NAMES[col.field]}`,
        dataField: col.field,
        onInput: (v) => (col.header = v),
      }),
    );

  const valueRows = <K extends string>(
    title: string,
    group: 'channel' | 'outcome' | 'spoke_with',
    keys: readonly K[],
    labels: Record<K, string>,
    map: Partial<Record<K, string>>,
  ) =>
    section(
      { title },
      keys.map((key) =>
        h(
          'div',
          { class: 'fieldrow' },
          h('span', { class: 'fieldrow__name' }, labels[key]),
          input({
            value: map[key] ?? labels[key],
            maxlength: 60,
            class: 'input--sm',
            ariaLabel: `Salesforce value for ${labels[key]}`,
            dataField: `${group}.${key}`,
            onInput: (v) => (map[key] = v),
          }),
        ),
      ),
    );

  const render = () => {
    if (!profile || disposed) return;
    const values = profile.values;
    values.channel ??= {};
    values.outcome ??= {};
    values.spoke_with ??= {};
    replaceChildren(
      host,
      h(
        'p',
        { class: 'activity__note' },
        'These are a guess at Square’s Salesforce until you see it. Change the headers to match the import template they give you, and the words to match their picklists.',
      ),
      section({ title: 'Columns' }, profile.columns.map(columnRow)),
      valueRows('How the touch happened', 'channel', TOUCH_CHANNELS, CHANNEL_LABELS, values.channel),
      valueRows('How it went', 'outcome', OUTCOMES, OUTCOME_LABELS, values.outcome),
      valueRows('Who he spoke with', 'spoke_with', SPOKE_WITH, SPOKE_WITH_LABELS, values.spoke_with),
      section(
        { title: 'Lead source' },
        input({
          value: profile.lead_source,
          maxlength: 120,
          ariaLabel: 'Lead source',
          dataField: 'lead_source',
          onInput: (v) => profile && (profile.lead_source = v),
        }),
      ),
      section(
        { title: 'The block to paste' },
        h('p', { class: 'card__summary' }, 'One of these per touch. {tokens} are the fields above.'),
        textarea({
          value: profile.block,
          maxlength: 2000,
          rows: 6,
          ariaLabel: 'The block to paste',
          dataField: 'block',
          onInput: (v) => profile && (profile.block = v),
        }),
      ),
      h(
        'div',
        { class: 'activity__actions' },
        btn('Back to the defaults', {
          variant: 'ghost',
          onClick: () => {
            profile = structuredClone(DEFAULT_EXPORT_PROFILE);
            render();
          },
        }),
      ),
      h('div', { class: 'actionbar' }, save),
    );
  };

  void loadExportProfile()
    .then((p) => {
      if (disposed) return;
      profile = structuredClone(p);
      render();
    })
    .catch((err: unknown) => {
      if (!disposed)
        replaceChildren(host, empty('The fields could not be read.', err instanceof Error ? err.message : ''));
    });

  return () => {
    disposed = true;
  };
};
